import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  accordionDetailsClasses,
  AccordionProps,
  AccordionSummary as MuiAccordionSummary,
  accordionSummaryClasses,
  AccordionSummaryProps,
  Box,
  styled,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router';
import { round } from '@kheops/utils';
import MinimumOrderValueChip from '../common/components/MinimumOrderValueChip';
import { BasketOrder } from '../hooks/useBasketOrders';
import useCurrencyFormat from '../hooks/useCurrencyFormat';
import { RoutePaths } from '../routes/AppRoutes';
import SizedImage from '../common/components/SizedImage';
import { ellipsisStyle } from '../common/utils/style.utils';
import BasketItemSummary from './BasketItemSummary';
import BulkRemoveFromBasketButton from './BulkRemoveFromBasketButton';
import CompanyLogo from '../common/components/CompanyLogo';
interface BasketOrderSummaryDrawerProps {
  order: BasketOrder;
  editable?: boolean;
}

export default function BasketOrderSummaryDrawer(
  { order, editable }: BasketOrderSummaryDrawerProps,
): React.JSX.Element {
  const { items, companyId, name, photoUrl, minimumOrderValue, minimumOrderValueType, totalPrice, minimumOrderValueProgression, brand } = order;
  const { t } = useTranslation(['basket', 'common']);
  const currencyFormat = useCurrencyFormat();
  const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} defaultExpanded {...props} />
  ))(({ theme }) => ({
    '&:before': {
      display: 'none',
    },
    backgroundColor: 'unset',
    [`& .${accordionDetailsClasses.root}`]: {
      padding: 0,
      paddingRight: theme.spacing(editable ? 1 : 0),
    },
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.875rem' }} />} {...props} />
  ))(({ theme }) => ({
    [`&.${accordionSummaryClasses.root}`]: {
      padding: 0,
      alignItems: 'start',
    },
    [`& .${accordionSummaryClasses.content}`]: {
      minWidth: 0,
      marginTop: 16,
      marginBottom: 16,
      alignItems: 'start',
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
      width: 44,
      marginTop: 20,
      justifyContent: 'center',
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
      transform: 'rotate(90deg)',
      position: 'absolute',
      right: 0,
      top: 0,
      color: theme.palette.onSurface.main,
    },
  }));

  return (
    <Accordion>
      <AccordionSummary>
        <SizedImage
          src={photoUrl}
          alt={t('common:company')}
          width={48}
          height={48}
          borderRadius={3}
          overlay={brand && (
              <CompanyLogo
                variant="square"
                size="small"
                brand={brand}
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  width: 24,
                  right: 4,
                }}
              />
            )}
        />
        <Box sx={{ ml: 1, minWidth: 0 }}>
          <Typography
            variant="bodyMedium"
            sx={{
              ...ellipsisStyle,
              pr: 4,
            }}
          >
            <Box
              component={Link}
              to={generatePath(RoutePaths.CONTRACT_COMPANY, { companyId })}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              {name}
            </Box>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 0.5,
            }}
          >
            <Typography variant="bodySmall">
              {t('common:total')} : {currencyFormat.format(round(totalPrice))}
            </Typography>
            <MinimumOrderValueChip
              sx={{
                width: 'fit-content',
              }}
              minimumOrderValue={minimumOrderValue}
              minimumOrderValueType={minimumOrderValueType}
              minimumOrderValueProgression={minimumOrderValueProgression}
              enableProgress
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {items.map((item) => {
          return (
            <BasketItemSummary
              item={item}
              key={item.packaging.id}
              editable={editable}
            />
          );
        })}
        <Box sx={{ width: '100%', textAlign: 'right' }}>
          <BulkRemoveFromBasketButton order={order} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
